import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import {
  Table,
  Input,
  Select,
  Button,
  Tag,
  Modal,
  Form,
  message,
  Spin,
  Checkbox,
  Row,
  Col,
  Space,
} from "antd";
import {
  SearchOutlined,
  PlusCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import EmployeeHistory from "./EmployeeHistory";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllDepartmentList,
  getLocations,
  getRoleList,
  getUserList,
  getUserInfo,
  getAssetsList,
  getClientid,
  getMenuList,
  getUserRoleId,
} from "redux/selectors";
import { CALL_API } from "common/API";
import { CSVLink } from "react-csv";
import { STATUS_CODE } from "common/Constants";
import { setAssetsList } from "redux/actions";
import { ButtonFilled, ButtonOutlined } from "reusableComponent/UIButtons";
import { DropdownCheckBox } from "atoms/UIDropdown/Dropdown";

const { Option } = Select;

/* ─── Styled ─────────────────────────────────────────────── */
const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 12px;
  // margin-bottom: 16px;

  .ant-input,
  .ant-select-selector {
    border-radius: 24px !important;
  }
  .ant-input::placeholder,
  .ant-select-selection-placeholder {
    color: #9ca3af !important;
  }
`;
const Left = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;
const Right = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  flex-shrink: 0;
`;
const LinkBtn = styled.span`
  color: #2563eb;
  cursor: pointer;
  font-size: 14px;
  white-space: nowrap;
  &:hover {
    text-decoration: underline;
  }
`;
const InServiceTag = styled(Tag)`
  background: #fff3e0;
  color: #e65100;
  border-color: #ffb74d;
  border-radius: 12px;
  font-size: 12px;
  padding: 2px 8px;
`;

/* ── Asset cell hover ── */
const CellWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 32px;
  padding: 4px 8px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s;

  .cell-icon {
    font-size: 17px;
    opacity: 0;
    transition: opacity 0.15s;
  }

  &:hover {
    background: ${({ $hasAsset }) => ($hasAsset ? "#fff1f0" : "#eff6ff")};
    .cell-icon {
      opacity: 1;
    }
  }
`;

/* ─── Asset Cell ─────────────────────────────────────────── */
const AssetCell = ({
  asset,
  onRevoke,
  onAdd,
  isHovered,
  onMouseEnter,
  onMouseLeave,
}) => {
  if (!asset) {
    return (
      <div
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={onAdd}
        style={{
          width: "100%",
          height: "100%",
          minHeight: 40,
          display: "flex",
          alignItems: "center",
          padding: "0 12px",
          background: isHovered ? (asset ? "#FFF1F0" : "") : "transparent",
          cursor: "pointer",
        }}
      >
        <PlusCircleOutlined
          style={{
            color: "#2563eb",
            opacity: isHovered ? 1 : 0,
            marginRight: 8,
          }}
        />
        <span style={{ color: "#bbb" }}>---</span>
      </div>
    );
  }

  const isInService = asset.asset_service_status === "In Service";

  return (
    <div
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onRevoke}
      style={{
        width: "100%",
        height: "100%",
        minHeight: 40,
        display: "flex",
        alignItems: "center",
        padding: "0 12px",
        background: isHovered ? "#EEF2FF" : "transparent",
        cursor: "pointer",
      }}
    >
      <span
        style={{
          fontWeight: 500,
          color: isHovered ? "#ef4444" : isInService ? "#e65100" : "#1a1a1a",
        }}
      >
        {asset.assetname}
      </span>

      {isInService && <InServiceTag>In Service</InServiceTag>}

      <CloseCircleOutlined
        style={{
          color: "#ef4444",
          marginLeft: "auto",
          opacity: isHovered ? 1 : 0,
        }}
      />
    </div>
  );
};

/* ─── PeopleView ─────────────────────────────────────────── */
const PeopleView = ({ exportRef }) => {
  const menuList = useSelector(getMenuList);
  const userRoleId = useSelector(getUserRoleId);

  const Menulist =
    menuList[userRoleId] || Object.values(menuList)[0] || menuList;
  const roleList = useSelector(getRoleList);
  const locationList = useSelector(getLocations);
  const departmentList = useSelector(getAllDepartmentList);
  const userList = useSelector(getUserList); // [{userid, fullname, employeeid, roleid, departmentid, locationid, ...}]
  const userInfo = useSelector(getUserInfo);
  const assetsList = useSelector(getAssetsList); // flat list from store
  const clientId = useSelector(getClientid);

  const [search, setSearch] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const [revokeLoading, setRevokeLoading] = useState(false);
  const [hoveredCell, setHoveredCell] = useState(null);

  // Filter form — same pattern as HRPerformance
  const [filterForm] = Form.useForm();
  const roleFilter = Form.useWatch("roleFilter", filterForm) || [];
  const deptFilter = Form.useWatch("deptFilter", filterForm) || [];
  const locFilter = Form.useWatch("locFilter", filterForm) || [];
  const catFilter = Form.useWatch("catFilter", filterForm) || [];

  // Category list for columns + assign modal filter
  const [categoryList, setCategoryList] = useState([]);
  const [catLoading, setCatLoading] = useState(false);

  // Assign modal
  const [assignModal, setAssignModal] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignForm] = Form.useForm();
  const [assignContext, setAssignContext] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkRevokeMode, setBulkRevokeMode] = useState(false);
  const [bulkRevokeModal, setBulkRevokeModal] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [bulkRevokeLoading, setBulkRevokeLoading] = useState(false);
  // { userid, categoryid, categoryname }
  const dispatch = useDispatch();

  const footerStyle = {
    position: "sticky",
    bottom: -20,
    left: 0,
    right: 0,
    backgroundColor: "#fff",
    padding: "10px",
    display: "flex",
    justifyContent: "end",
    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
    margin: "0px -20px",
    zIndex: 10,
  };

  const handleBulkRevoke = async () => {
    try {
      setRevokeLoading(true);
      const { code } = await CALL_API(
        `/assets/people-view/bulk-revoke`,
        "patch",
        {
          assigned_to: selectedRowKeys,
          categoryid: selectedCategories,
        }
      );
      if (code === STATUS_CODE.SUCCESS) {
        await fetchMasterData(true);
        setBulkRevokeMode(false);
        setBulkRevokeModal(false);
        setSelectedCategories([]);
        message.success("Asset revoked successfully");
        // NOTE: dispatch a refresh of assetsList in your store or reload
      } else {
        throw new Error();
      }
    } catch {
      message.error("Failed to revoke asset");
    } finally {
      setRevokeLoading(false);
    }
  };
  const fetchMasterData = async (fetch_master_data) => {
    const { code, assetsCategories } = await CALL_API(
      `assets/getassets/all`,
      "get"
    );
    if (code === STATUS_CODE.SUCCESS) {
      dispatch(setAssetsList(assetsCategories));
    }
  };

  /* ── Fetch categories (for column headers) ── */
  const getCategories = async () => {
    try {
      setCatLoading(true);
      const { code, categoryList: list } = await CALL_API(
        `/assets/categorytypes/all`,
        "get"
      );
      if (code === STATUS_CODE.SUCCESS)
        setCategoryList(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error(err);
    } finally {
      setCatLoading(false);
    }
  };

  useEffect(() => {
    getCategories();
  }, []);

  const [csvData, setCsvData] = useState([]);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const csvLinkRef = useRef(null);

  // Register CSV export for this tab
  useEffect(() => {
    if (exportRef) {
      exportRef.current = () => {
        if (!filtered.length) {
          message.error("No data available to download.");
          return;
        }
        const headers = [
          { label: "Employee Name", key: "employeeName" },
          { label: "Employee ID", key: "employeeId" },
          ...derivedCategories.map((cat) => ({
            label: cat.categoryname,
            key: `cat_${cat.categoryid}`,
          })),
        ];
        const rows = filtered.map((p) => {
          const row = {
            employeeName: p.fullname || p.name || "",
            employeeId: p.employeeid || p.userid || "",
          };
          derivedCategories.forEach((cat) => {
            const asset = p.assetsByCategory[cat.categoryid];
            row[`cat_${cat.categoryid}`] = asset ? asset.assetname : "";
          });
          return row;
        });
        setCsvHeaders(headers);
        setCsvData(rows);
        setTimeout(() => csvLinkRef.current?.link?.click());
      };
    }
  });

  /* ── Derive unique categories from assetsList as fallback ── */
  const derivedCategories = useMemo(() => {
    if (categoryList.length) return categoryList;
    const seen = new Map();
    (assetsList || []).forEach((a) => {
      if (a.categoryid && !seen.has(a.categoryid))
        seen.set(a.categoryid, {
          categoryid: a.categoryid,
          categoryname: a.categoryname,
        });
    });
    return [...seen.values()];
  }, [categoryList, assetsList]);

  /* ── Build people rows: merge userList + assetsList ── */
  const peopleRows = useMemo(() => {
    return (userList?.filter((item) => item.statusid === "1") || []).map(
      (user) => {
        // collect all assets assigned to this user
        const userAssets = (assetsList || []).filter(
          (a) => a.assigned_to === (user.userid || user.employeeid)
        );
        // map categoryid → asset (one per category per user)
        const assetsByCategory = {};
        userAssets.forEach((a) => {
          assetsByCategory[a.categoryid] = a;
        });
        return { ...user, assetsByCategory };
      }
    );
  }, [userList, assetsList]);

  /* ── Unassigned assets for assign modal dropdown ── */
  const getUnassignedOptions = (categoryid) =>
    (assetsList || []).filter(
      (a) => a.categoryid === categoryid && !a.assigned_to
    );

  /* ── Open assign modal ── */
  const openAssignModal = (userid, category) => {
    assignForm.resetFields();
    setAssignContext({
      userid,
      categoryid: category.categoryid,
      categoryname: category.categoryname,
    });
    setAssignModal(true);
  };

  /* ── Assign submit ── */
  const handleAssign = async () => {
    try {
      const values = await assignForm.validateFields();
      const selectedAsset = (assetsList || []).find(
        (item) => item.assetid === values.assetid
      );
      setAssignLoading(true);
      const { code } = await CALL_API(
        `/assets/assignassets/${values.assetid}`,
        "patch",
        {
          assigned_to: assignContext?.userid,
          assigned_on: new Date().toISOString(),
          assigned_by: userInfo?.userid || "",
          assetstatus: "Assigned",
          categorytype: selectedAsset?.categorytype,
          model: selectedAsset?.model,
          assetname: selectedAsset?.assetname,
          categoryid: selectedAsset?.categoryid || assignContext?.categoryid,
          categoryname:
            selectedAsset?.categoryname || assignContext?.categoryname,
          masterid: selectedAsset?.masterid,
        }
      );
      if (code === STATUS_CODE.SUCCESS) {
        await fetchMasterData(true);
        message.success("Asset assigned successfully");
        setAssignModal(false);
        assignForm.resetFields();
        // NOTE: dispatch a refresh of assetsList in your store or reload
      } else {
        throw new Error(`Failed: ${code}`);
      }
    } catch (err) {
      if (err?.errorFields) return;
      message.error("Failed to assign asset");
    } finally {
      setAssignLoading(false);
    }
  };

  /* ── Revoke confirm ── */
  const confirmRevoke = (asset) => {
    Modal.confirm({
      title: "Revoke Asset",
      content: `Are you sure you want to revoke ${asset.assetname}?`,
      okText: "Revoke",
      cancelText: "Cancel",
      centered: true,
      onOk: async () => {
        try {
          setRevokeLoading(true);
          const { code } = await CALL_API(
            `/assets/revokeassets/${asset.assetid}`,
            "patch",
            {
              revoked_by: userInfo?.userid,
              revoked_on: new Date().toISOString(),
              assetstatus: "Available",
              assigned_to: asset?.assigned_to,
            }
          );
          if (code === STATUS_CODE.SUCCESS) {
            await fetchMasterData(true);
            message.success("Asset revoked successfully");
            // NOTE: dispatch a refresh of assetsList in your store or reload
          } else {
            throw new Error();
          }
        } catch {
          message.error("Failed to revoke asset");
        } finally {
          setRevokeLoading(false);
        }
      },
    });
  };

  /* ── Filters ── */
  console.log("peopleRows", peopleRows);
  const filtered = peopleRows.filter((p) => {
    const name = (p.fullname || p.name || "").toLowerCase();
    const empId = (p.employeeid || p.userid || "").toLowerCase();

    // Search
    if (
      search &&
      !name.includes(search.toLowerCase()) &&
      !empId.includes(search.toLowerCase())
    ) {
      return false;
    }

    // Role Filter
    if (
      roleFilter.length &&
      !p.roleid?.some((role) => roleFilter.includes(role))
    ) {
      return false;
    }

    // Department Filter
    const departmentIds = Object.values(p.userdepartmentid || {}).flat();

    if (
      deptFilter.length &&
      !departmentIds.some((dept) => deptFilter.includes(dept))
    ) {
      return false;
    }

    // Location Filter
    const locationIds = [
      p.baselocationid,
      ...Object.values(p.userlocationid || {}).flat(),
    ].filter(Boolean);

    if (
      locFilter.length &&
      !locationIds.some((loc) => locFilter.includes(loc))
    ) {
      return false;
    }

    // Category Filter
    if (
      catFilter.length &&
      !catFilter.some((cid) => p.assetsByCategory?.[cid])
    ) {
      return false;
    }

    return true;
  });

  /* ── Build dynamic category columns ── */
  const categoryColumns = derivedCategories.map((cat) => ({
    title: cat.categoryname,
    key: `cat_${cat.categoryid}`,
    width: 180,

    onCell: (record) => ({
      style: {
        padding: 0,
        background:
          hoveredCell?.rowId === (record.userid || record.employeeid) &&
          hoveredCell?.categoryId === cat.categoryid
            ? "#EEF2FF"
            : undefined,
      },
    }),

    render: (_, row) => {
      const asset = row.assetsByCategory[cat.categoryid] || null;

      const rowId = row.userid || row.employeeid;

      return (
        <AssetCell
          asset={asset}
          isHovered={
            hoveredCell?.rowId === rowId &&
            hoveredCell?.categoryId === cat.categoryid
          }
          onMouseEnter={() =>
            setHoveredCell({
              rowId,
              categoryId: cat.categoryid,
            })
          }
          onMouseLeave={() => setHoveredCell(null)}
          onRevoke={() => {
            if (
              Menulist?.find(
                (item) =>
                  item?.key === "HRResources" &&
                  item?.subMenu?.find(
                    (subItem) =>
                      subItem?.key === "assets" && subItem?.permission?.edit
                  )
              )
            ) {
              confirmRevoke(asset);
            }
          }}
          onAdd={() => {
            if (
              Menulist?.find(
                (item) =>
                  item?.key === "HRResources" &&
                  item?.subMenu?.find(
                    (subItem) =>
                      subItem?.key === "assets" && subItem?.permission?.create
                  )
              )
            ) {
              openAssignModal(rowId, cat);
            }
          }}
        />
      );
    },
  }));

  const selectedEmployees = filtered.filter((item) =>
    selectedRowKeys.includes(item.userid || item.employeeid)
  );
  const selectionColumn = {
    title: (
      <Checkbox
        checked={
          filtered.length > 0 && selectedRowKeys.length === filtered.length
        }
        indeterminate={
          selectedRowKeys.length > 0 && selectedRowKeys.length < filtered.length
        }
        onChange={(e) => {
          if (e.target.checked) {
            setSelectedRowKeys(
              filtered.map((item) => item.userid || item.employeeid)
            );
          } else {
            setSelectedRowKeys([]);
          }
        }}
      />
    ),
    key: "selection",
    width: 70,
    align: "center",
    fixed: "right",
    render: (_, record) => {
      const rowId = record.userid || record.employeeid;

      return (
        <Checkbox
          checked={selectedRowKeys.includes(rowId)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedRowKeys((prev) => [...prev, rowId]);
            } else {
              setSelectedRowKeys((prev) => prev.filter((id) => id !== rowId));
            }
          }}
        />
      );
    },
  };
  const columns = [
    {
      title: "Employee Name",
      key: "name",
      fixed: "left",
      width: 200,
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 500 }}>{r.fullname || r.name}</div>
          <div style={{ color: "#888", fontSize: 12 }}>
            {r.employeeid || r.userid}
          </div>
        </div>
      ),
    },

    ...categoryColumns,

    ...(bulkRevokeMode ? [selectionColumn] : []),
  ];

  if (showHistory)
    return <EmployeeHistory onBack={() => setShowHistory(false)} />;

  return (
    <div>
      {/* Hidden CSV link */}
      <CSVLink
        headers={csvHeaders}
        data={csvData}
        filename="people_view.csv"
        ref={csvLinkRef}
        style={{ display: "none" }}
      />
      {/* ── Toolbar ── */}
      <Form form={filterForm} layout="vertical">
        {/* Row 1 - Filters */}
        <Row gutter={[12, 12]} style={{ marginBottom: 0 }}>
          <Col xs={24} sm={12} md={8} lg={5}>
            <Input
              prefix={<SearchOutlined />}
              placeholder="Search People"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Col>

          <Col xs={24} sm={12} md={8} lg={4}>
            <Form.Item name="roleFilter" style={{ marginBottom: 0 }}>
              <DropdownCheckBox
                name="roleFilter"
                label="Role"
                form={filterForm}
                options={roleList.map((r) => ({
                  label: r.roleName,
                  value: r.roleId,
                }))}
                handleFormChange={() => {}}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8} lg={4}>
            <Form.Item name="deptFilter" style={{ marginBottom: 0 }}>
              <DropdownCheckBox
                name="deptFilter"
                label="Department"
                form={filterForm}
                options={departmentList.map((d) => ({
                  label: d.department_name,
                  value: d.department_id,
                }))}
                handleFormChange={() => {}}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8} lg={4}>
            <Form.Item name="locFilter" style={{ marginBottom: 0 }}>
              <DropdownCheckBox
                name="locFilter"
                label="Location"
                form={filterForm}
                options={locationList
                  ?.filter((item) => item?.login_from_anywhere !== 1)
                  ?.map((l) => ({
                    label: l.location_name,
                    value: l.location_id,
                  }))}
                handleFormChange={() => {}}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8} lg={4}>
            <Form.Item name="catFilter" style={{ marginBottom: 0 }}>
              <DropdownCheckBox
                name="catFilter"
                label="Category"
                form={filterForm}
                options={derivedCategories.map((c) => ({
                  label: c.categoryname,
                  value: c.categoryid,
                }))}
                handleFormChange={() => {}}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Row 2 - Count & Actions */}
        <Row justify="space-between" align="middle" className="mb-2">
          <Col>
            <span
              style={{
                fontWeight: 500,
                fontSize: 14,
              }}
            >
              Total Employees: {filtered.length}
            </span>
          </Col>

          <Col>
            <Space size={20}>
              <LinkBtn onClick={() => setShowHistory(true)}>
                Employee History
              </LinkBtn>
              {Menulist?.find(
                (item) =>
                  item?.key === "HRResources" &&
                  item?.subMenu?.find(
                    (subItem) =>
                      subItem?.key === "assets" && subItem?.permission?.edit
                  )
              ) && (
                <LinkBtn
                  onClick={() => {
                    setBulkRevokeMode(true);
                    setSelectedRowKeys([]);
                  }}
                >
                  Bulk Revoke
                </LinkBtn>
              )}
            </Space>
          </Col>
        </Row>
      </Form>
      {/* ── Table ── */}
      <Spin spinning={catLoading || revokeLoading}>
        <div style={{ overflowX: "auto" }}>
          <Table
            columns={columns}
            dataSource={filtered}
            pagination={false}
            size="middle"
            rowKey={(r) => r.userid || r.employeeid}
            scroll={{ x: "max-content" }}
            style={{ border: "1px solid #f0f0f0", borderRadius: 8 }}
          />
        </div>
      </Spin>
      {selectedRowKeys?.length > 0 && (
        <div style={footerStyle}>
          <ButtonOutlined
            style={{ borderRadius: "5px" }}
            onClick={() => {
              setSelectedRowKeys([]);
              setBulkRevokeMode(false);
            }}
          >
            Cancel
          </ButtonOutlined>
          <ButtonFilled
            style={{ borderRadius: "5px" }}
            type="primary"
            loading={bulkRevokeLoading}
            onClick={() => {
              setBulkRevokeModal(true);
            }}
          >
            Next
          </ButtonFilled>
        </div>
      )}

      <Modal
        open={bulkRevokeModal}
        footer={null}
        onCancel={() => {
          setBulkRevokeMode(false);
          setBulkRevokeModal(false);
          setSelectedCategories([]);
        }}
        centered
        width={580}
        closable
      >
        <div>
          <h3
            style={{
              marginBottom: 24,
              fontSize: 28,
              fontWeight: 600,
              color: "#1F2937",
            }}
          >
            Revoking Assets
          </h3>

          <div
            style={{
              marginBottom: 10,
              fontSize: 16,
              color: "#4B5563",
            }}
          >
            Choose category you want to revoke
          </div>

          <Select
            mode="multiple"
            allowClear
            placeholder="Select Categories"
            value={selectedCategories}
            onChange={setSelectedCategories}
            style={{
              width: "100%",
              marginBottom: 120,
            }}
            maxTagCount="responsive"
          >
            {derivedCategories.map((cat) => (
              <Select.Option key={cat.categoryid} value={cat.categoryid}>
                {cat.categoryname}
              </Select.Option>
            ))}
          </Select>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 12,
            }}
          >
            <Button
              style={{
                minWidth: 130,
                height: 40,
                borderRadius: 8,
                color: "#2563EB",
                borderColor: "#2563EB",
              }}
              onClick={() => {
                setBulkRevokeMode(false);
                setBulkRevokeModal(false);
                setSelectedCategories([]);
              }}
            >
              Cancel
            </Button>

            <Button
              type="primary"
              disabled={!selectedCategories.length}
              style={{
                minWidth: 130,
                height: 40,
                borderRadius: 8,
                background: "#3156E0",
                borderColor: "#3156E0",
              }}
              onClick={() => {
                handleBulkRevoke();
              }}
            >
              Revoke
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── Assign Asset Modal ── */}
      <Modal
        title={`Assigning Asset – ${assignContext?.categoryname || ""}`}
        open={assignModal}
        onCancel={() => {
          setAssignModal(false);
          assignForm.resetFields();
        }}
        centered
        width={440}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setAssignModal(false);
              assignForm.resetFields();
            }}
          >
            Cancel
          </Button>,
          <Button
            key="assign"
            type="primary"
            loading={assignLoading}
            style={{ background: "#2563eb" }}
            onClick={handleAssign}
          >
            Assign
          </Button>,
        ]}
      >
        <Form form={assignForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            label="Choose Asset"
            name="assetid"
            rules={[{ required: true, message: "Please select an asset" }]}
          >
            <Select
              placeholder="Select asset"
              style={{ width: "100%" }}
              showSearch
              optionFilterProp="children"
            >
              {getUnassignedOptions(assignContext?.categoryid).map((a) => (
                <Option key={a.assetid} value={a.assetid}>
                  {a.assetname}
                  {a.model
                    ? ` – ${Array.isArray(a.model) ? a.model[0] : a.model}`
                    : ""}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
        {getUnassignedOptions(assignContext?.categoryid).length === 0 && (
          <div
            style={{
              color: "#888",
              fontSize: 13,
              textAlign: "center",
              padding: "8px 0",
            }}
          >
            No unassigned assets available for this category.
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PeopleView;
